import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as moment from 'moment';
import {formatDate} from '@angular/common';
import {ValidatorService} from '@utils-lib';
import {defineLocale, deLocale} from 'ngx-bootstrap/chronos';
import {BsLocaleService} from 'ngx-bootstrap/datepicker';

defineLocale('de', deLocale);

@Component({
    selector: 'date-picker',
    templateUrl: 'date-picker.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DatePickerComponent
        }
    ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {

    @ViewChild('targetDatePicker')
    public targetDatePicker;

    @Input()
    public id: string;

    @Input()
    public messagePath: string;

    public internalRequired: boolean;

    @Input()
    public maxDate: Date;

    @Input()
    public minDate: Date;

    @Input()
    public checkAdditionalControls = [];

    datepickerForm?: FormGroup;
    touched = false;
    hasFocus = false;
    dateValidators = [this.validatorService.date(), this.validatorService.conditionalValidator(
        () => this.isRequired, this.validatorService.required())];

    constructor(private readonly formBuilder: FormBuilder,
                private readonly localeService: BsLocaleService,
                private readonly validatorService: ValidatorService) {
    }

    private static getDate(value: string): Date {
        if (value === null) {
            return null;
        }
        try {
            return moment(value, 'DD.MM.YYYY', true).toDate();
        } catch (error) {
            return null;
        }
    }

    private static getString(value: Date): string {
        if (value === null) {
            return null;
        }
        try {
            return formatDate(value, 'dd.MM.yyyy', 'de-DE');
        } catch (error) {
            return null;
        }
    }


    public get isRequired(): boolean {
        return this.internalRequired;
    }

    @Input()
    public set isRequired(data: boolean) {
        this.internalRequired = data;
        if (this.datepickerForm !== undefined) {
            this.datepickerForm.get('date').clearValidators();
            if (data) {
                this.datepickerForm.get('date').addValidators(this.dateValidators);
            }
            this.datepickerForm.get('date').updateValueAndValidity();
        }
    }

    public ngOnInit(): void {
        deLocale.invalidDate = '';
        defineLocale('custom', deLocale);
        this.localeService.use('custom');

       this.datepickerForm = this.formBuilder.group({
            date: [null, this.dateValidators]
        });
        this.datepickerForm.controls.date.valueChanges.subscribe(() => this.onValueChange());
    }

    onChange(_value?): void {
      // This is intentional
    }

    onTouched(): void {
      // This is intentional
    }

    writeValue(value: string): void {
        this.datepickerForm.controls.date.setValue(DatePickerComponent.getDate(value));
    }

    registerOnChange(onChange: any): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any): void {
        this.onTouched = onTouched;
    }

    setDisabledState?(disabled: boolean): void {
        if (disabled) {
            this.datepickerForm.disable();
        } else {
            this.datepickerForm.enable();
        }
    }

    onDatePickerKeyup(value: string): void {
        if (value === '') {
            this.targetDatePicker.nativeElement.blur();
            this.targetDatePicker.nativeElement.click();
        }
    }

    onFocus(): void {
      const timeOutLength = 250;
        if (!this.hasFocus) {
            this.hasFocus = true;
            this.targetDatePicker?.nativeElement?.blur();
            setTimeout(() => {
                this.targetDatePicker?.nativeElement?.focus();
                this.targetDatePicker?.nativeElement?.click();
            });
            setTimeout(() => {
                this.targetDatePicker?.nativeElement?.click();
            }, timeOutLength);
        }
    }

    private markAsTouched(): void {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private onValueChange(): void {
        this.markAsTouched();
        const currentValue = this.datepickerForm.controls.date.value;
        if (this.maxDate !== null && currentValue > this.maxDate) {
            this.datepickerForm.controls.date.setValue(this.maxDate);
            return;
        }
        if (this.minDate !== null && currentValue < this.minDate) {
            this.datepickerForm.controls.date.setValue(this.minDate);
            return;
        }
        this.onChange(DatePickerComponent.getString(this.datepickerForm.controls.date.value));
    }

}
